/**
 * V1.8.9 — Three.js visual-effect helpers extracted from ThreeViewport.
 *
 * Everything in this module is PURE JS — no React, no hooks, no state.
 * The React component calls these from the master tick loop or the
 * rebuild effect. Keeping them out of the component reduces the viewport
 * file size considerably (1438 LOC → ~1130 LOC) and makes the effects
 * individually testable.
 *
 * Exports:
 *   - disposeNodeStatic(node)                  — rebuild error-cleanup
 *   - applyInteriorWallVisibility(group, show) — toggle interior walls
 *   - applyInteriorOcclusion(group, camera)    — X-Ray walls (V1.5)
 *   - restoreInteriorOpacity(group)            — revert X-Ray walls
 *   - applyDraftingMode(group, scene, st, on)  — Whiteprint aesthetic (V1.6)
 *   - applyGhostWall(group, camera, controls)  — Seamless Nav ray-fade
 *   - applyDollhouseFade(group, camera)        — Seamless Nav distance-fade
 *   - commitSeamlessNavOpacity(group, enabled) — apply combined fades
 *   - tmpV3                                    — shared reusable Vector3
 *
 * Plus the Drafting Mode constants for callers that want to read them.
 */
import * as THREE from "three";

// Reusable Vector3 to avoid per-frame allocations across helpers.
export const tmpV3 = new THREE.Vector3();
const _ghostRay = new THREE.Raycaster();

// --- Generic dispose -------------------------------------------------------
export function disposeNodeStatic(n) {
  n?.geometry?.dispose?.();
  if (Array.isArray(n?.material)) n.material.forEach((m) => m.dispose?.());
  else n?.material?.dispose?.();
}

// --- Interior wall visibility toggle --------------------------------------
export function applyInteriorWallVisibility(houseGroup, show) {
  if (!houseGroup) return;
  houseGroup.traverse((node) => {
    const k = node.userData?.kind;
    if (k !== "wall" && k !== "wall_door" && k !== "wall_window") return;
    if (node.userData.isExterior) return;
    node.visible = !!show;
  });
}

// --- X-Ray interior occlusion (V1.5) --------------------------------------
const OCCLUDE_NEAR = 1.5;
const OCCLUDE_FAR = 5.0;
const OCCLUDE_MIN_OPACITY = 0.30;
const OCCLUDE_LERP = 0.18;

export function applyInteriorOcclusion(houseGroup, camera) {
  if (!houseGroup || !camera) return;
  const camPos = camera.position;
  houseGroup.traverse((node) => {
    if (!node.isMesh) return;
    const k = node.userData?.kind;
    if (k !== "wall" && k !== "wall_door" && k !== "wall_window") return;
    if (node.userData.isExterior) return;
    const mats = Array.isArray(node.material) ? node.material : [node.material];
    const wp = node.getWorldPosition(tmpV3);
    const d = wp.distanceTo(camPos);
    let target = 1.0;
    if (d <= OCCLUDE_NEAR) {
      target = OCCLUDE_MIN_OPACITY;
    } else if (d < OCCLUDE_FAR) {
      const t = (d - OCCLUDE_NEAR) / (OCCLUDE_FAR - OCCLUDE_NEAR);
      target = OCCLUDE_MIN_OPACITY + (1.0 - OCCLUDE_MIN_OPACITY) * t;
    }
    mats.forEach((m) => {
      if (!m) return;
      if (m.userData._origOpacity == null) m.userData._origOpacity = m.opacity ?? 1.0;
      const cur = m.opacity ?? 1.0;
      const next = cur + (target - cur) * OCCLUDE_LERP;
      m.opacity = next;
      const shouldBeTransparent = next < 0.99;
      if (shouldBeTransparent) {
        if (!m.transparent) {
          m.transparent = true;
          m.depthWrite = false;
        }
      } else if (m.userData._origTransparent === undefined) {
        m.userData._origTransparent = m.transparent === true;
      }
    });
  });
}

export function restoreInteriorOpacity(houseGroup) {
  if (!houseGroup) return;
  houseGroup.traverse((node) => {
    if (!node.isMesh) return;
    const k = node.userData?.kind;
    if (k !== "wall" && k !== "wall_door" && k !== "wall_window") return;
    if (node.userData.isExterior) return;
    const mats = Array.isArray(node.material) ? node.material : [node.material];
    mats.forEach((m) => {
      if (!m) return;
      if (m.userData._origOpacity != null) {
        m.opacity = m.userData._origOpacity;
        m.transparent = m.userData._origTransparent === true;
        m.depthWrite = !m.transparent;
      }
    });
  });
}

// --- Drafting Mode (Whiteprint) -------------------------------------------
export const DRAFTING_BG = 0x0041ba;
const DRAFTING_FILL = 0xffffff;
const DRAFTING_EDGE_DARK = 0x041a4f;
const DRAFTING_EDGE_BLACK = 0x000000;
const DRAFTING_EDGE_THRESHOLD_DEG = 18;
const DRAFTING_OVERLAY_GROUP_NAME = "__drafting_outlines";

export function applyDraftingMode(houseGroup, scene, sceneState, enabled) {
  if (!houseGroup || !scene) return;
  const drafting = !!enabled;
  if (drafting) {
    if (!sceneState._origBg) {
      sceneState._origBg = scene.background;
      sceneState._origFog = scene.fog;
    }
    scene.background = new THREE.Color(DRAFTING_BG);
    scene.fog = null;
  } else if (sceneState._origBg) {
    scene.background = sceneState._origBg;
    scene.fog = sceneState._origFog;
    sceneState._origBg = null;
    sceneState._origFog = null;
  }
  const sky = scene.userData?._skyMesh;
  if (sky) sky.visible = !drafting;
  const grid = scene.userData?._grid;
  if (grid) grid.visible = !drafting;
  if (sceneState.envGroup) sceneState.envGroup.visible = !drafting;
  if (sceneState.sun) {
    if (drafting) {
      if (sceneState._origSunIntensity == null) sceneState._origSunIntensity = sceneState.sun.intensity;
      sceneState.sun.intensity = 0;
      sceneState.sun.castShadow = false;
    } else if (sceneState._origSunIntensity != null) {
      sceneState.sun.intensity = sceneState._origSunIntensity;
      sceneState.sun.castShadow = true;
      sceneState._origSunIntensity = null;
    }
  }
  if (sceneState.hemi) {
    if (drafting) {
      if (sceneState._origHemiIntensity == null) sceneState._origHemiIntensity = sceneState.hemi.intensity;
      sceneState.hemi.intensity = 0;
    } else if (sceneState._origHemiIntensity != null) {
      sceneState.hemi.intensity = sceneState._origHemiIntensity;
      sceneState._origHemiIntensity = null;
    }
  }
  if (sceneState.ambient) {
    sceneState.ambient.intensity = drafting ? 1.0 : 0;
  }
  if (sceneState.renderer) {
    sceneState.renderer.shadowMap.enabled = !drafting;
  }
  const existingOverlay = houseGroup.getObjectByName(DRAFTING_OVERLAY_GROUP_NAME);
  if (existingOverlay) {
    existingOverlay.traverse((c) => {
      c.geometry?.dispose?.();
      if (Array.isArray(c.material)) c.material.forEach((m) => m.dispose?.());
      else c.material?.dispose?.();
    });
    houseGroup.remove(existingOverlay);
  }
  const overlay = drafting ? new THREE.Group() : null;
  if (overlay) overlay.name = DRAFTING_OVERLAY_GROUP_NAME;
  houseGroup.traverse((node) => {
    if (!node.isMesh) return;
    if (node.parent && node.parent.name === DRAFTING_OVERLAY_GROUP_NAME) return;
    const mats = Array.isArray(node.material) ? node.material : [node.material];
    if (drafting) {
      if (!node.userData._origMaterial) {
        node.userData._origMaterial = node.material;
        node.userData._origCastShadow = node.castShadow;
        node.userData._origReceiveShadow = node.receiveShadow;
      }
      const isGlass = mats.some((m) => m && (m.transparent || (m.opacity ?? 1) < 0.95));
      const fillMat = new THREE.MeshBasicMaterial({
        color: DRAFTING_FILL,
        transparent: isGlass,
        opacity: isGlass ? 0.18 : 1.0,
        depthWrite: !isGlass,
      });
      node.material = fillMat;
      node.castShadow = false;
      node.receiveShadow = false;
      try {
        const isExterior = !!node.userData?.isExteriorShell;
        const edgeColor = isExterior ? DRAFTING_EDGE_BLACK : DRAFTING_EDGE_DARK;
        const edges = new THREE.EdgesGeometry(node.geometry, DRAFTING_EDGE_THRESHOLD_DEG);
        const lineMat = new THREE.LineBasicMaterial({
          color: edgeColor,
          linewidth: isExterior ? 2 : 1,
          transparent: false,
        });
        const lines = new THREE.LineSegments(edges, lineMat);
        node.matrixWorld.decompose(lines.position, lines.quaternion, lines.scale);
        overlay.add(lines);
      } catch {
        // EdgesGeometry can throw on degenerate geometry — skip gracefully.
      }
    } else if (node.userData._origMaterial) {
      node.material = node.userData._origMaterial;
      node.castShadow = node.userData._origCastShadow ?? true;
      node.receiveShadow = node.userData._origReceiveShadow ?? true;
      node.userData._origMaterial = null;
    }
  });
  if (overlay) houseGroup.add(overlay);
}

// --- Seamless Nav: ghost wall + dollhouse fade (V1.5.2) -------------------
export function applyGhostWall(houseGroup, camera, controls) {
  if (!houseGroup || !camera || !controls) return;
  const dir = tmpV3.copy(controls.target).sub(camera.position);
  const dist = dir.length();
  if (dist < 0.05) return;
  dir.normalize();
  _ghostRay.set(camera.position, dir);
  _ghostRay.far = dist;
  const candidates = [];
  houseGroup.traverse((node) => {
    if (!node.isMesh) return;
    if (!node.userData?.isExteriorShell) return;
    candidates.push(node);
  });
  if (candidates.length === 0) return;
  const intersects = _ghostRay.intersectObjects(candidates, false);
  const hitSet = new Set(intersects.map((it) => it.object.uuid));
  candidates.forEach((mesh) => {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const target = hitSet.has(mesh.uuid) ? 0.2 : 1.0;
    mats.forEach((m) => {
      if (!m) return;
      if (m.userData._origOpacity == null) m.userData._origOpacity = m.opacity ?? 1.0;
      m.userData._ghostTarget = target;
    });
  });
}

export function applyDollhouseFade(houseGroup, camera) {
  if (!houseGroup || !camera) return;
  const box = new THREE.Box3().setFromObject(houseGroup);
  const center = box.getCenter(tmpV3);
  const d = camera.position.distanceTo(center);
  let dollTarget = 1.0;
  if (d <= 30) dollTarget = 0.4;
  else if (d < 50) dollTarget = 0.4 + (1.0 - 0.4) * ((d - 30) / 20);
  houseGroup.traverse((node) => {
    if (!node.isMesh) return;
    if (!node.userData?.isExteriorShell) return;
    const mats = Array.isArray(node.material) ? node.material : [node.material];
    mats.forEach((m) => {
      if (!m) return;
      if (m.userData._origOpacity == null) m.userData._origOpacity = m.opacity ?? 1.0;
      m.userData._dollTarget = dollTarget;
    });
  });
}

const SEAMLESS_NAV_LERP = 0.16;
export function commitSeamlessNavOpacity(houseGroup, enabled) {
  if (!houseGroup) return;
  houseGroup.traverse((node) => {
    if (!node.isMesh) return;
    if (!node.userData?.isExteriorShell) return;
    const mats = Array.isArray(node.material) ? node.material : [node.material];
    mats.forEach((m) => {
      if (!m) return;
      let target = 1.0;
      if (enabled) {
        const ghost = m.userData._ghostTarget;
        const doll = m.userData._dollTarget;
        if (ghost != null && doll != null) target = Math.min(ghost, doll);
        else if (ghost != null) target = ghost;
        else if (doll != null) target = doll;
      }
      const cur = m.opacity ?? 1.0;
      const next = cur + (target - cur) * SEAMLESS_NAV_LERP;
      m.opacity = next;
      const shouldBeTransparent = next < 0.99;
      if (shouldBeTransparent) {
        if (!m.transparent) {
          m.transparent = true;
          m.depthWrite = false;
        }
      } else if (m.transparent && Math.abs(next - 1.0) < 0.01) {
        m.transparent = m.userData._origTransparent === true;
        m.depthWrite = !m.transparent;
        m.opacity = m.userData._origOpacity ?? 1.0;
      }
    });
  });
}
